import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useConfirm } from '../../utils/useConfirm';

import EmployeeRow from './components/EmployeeRow';
import CancelPayRunModal from './components/CancelPayRunModal';
import RunPayrollHeader from './components/RunPayrollHeader';
import RunPayrollToolbar from './components/RunPayrollToolbar';
import RunPayrollModals from './components/RunPayrollModals';

import { usePayRunData } from './usePayRun';
import { useAutosave } from './useAutosave';
import { authHeaders, rowAmounts, mapEmployeeToRow } from './helpers';
import { API_URL, FONT, GRID_COLS } from './constants';
import { C } from './styles';
import type {
  Row, PayRun, Schedule,
  StatusKey, StatHolidayOverrides, StatSubModal,
} from './types';

/* eslint-disable @typescript-eslint/no-explicit-any */
const useConfirmAny = useConfirm as any;
/* eslint-enable @typescript-eslint/no-explicit-any */

// Build test marker (parity with legacy)
// eslint-disable-next-line no-console
console.log('NOVALA_BUILD_TEST_' + Date.now());

export default function RunPayroll() {
  const { payRunId } = useParams<{ payRunId: string }>();
  const confirm = useConfirmAny();
  const navigate = useNavigate();

  const [error, setError] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);

  const [payRun, setPayRun] = useState<PayRun | null>(null);
  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);

  const [statHolidayPopupOpen, setStatHolidayPopupOpen] = useState<boolean>(false);
  const [statHolidayApplied, setStatHolidayApplied] = useState<boolean | null>(null);
  const [statHolidayOverrides, setStatHolidayOverrides] = useState<StatHolidayOverrides>({});
  const [statSubModal, setStatSubModal] = useState<StatSubModal | null>(null);
  const [changePeriodOpen, setChangePeriodOpen] = useState<boolean>(false);

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchExpanded, setSearchExpanded] = useState<boolean>(false);
  const [openMenuId, setOpenMenuId] = useState<string | number | null>(null);
  const [selectedRowId, setSelectedRowId] = useState<string | number | null>(null);
  const [editDrawerEmployeeId, setEditDrawerEmployeeId] = useState<string | number | null>(null);
  const [openPayMethodId, setOpenPayMethodId] = useState<string | number | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusKey[]>([]);

  const autosave = useAutosave(payRunId);

  const { data: loadedData, isLoading: queryLoading, error: queryError } = usePayRunData(payRunId);

  useEffect(() => {
    if (queryError) setError(queryError.message);
  }, [queryError]);

  useEffect(() => {
    if (!loadedData) return;
    const { runData, schedData, empArr, lines } = loadedData;
    setPayRun(runData);
    if (schedData) setSchedule(schedData);

    const linesByEmp: Record<string, any> = {};
    lines.forEach((l) => { if (l.employee_id != null) linesByEmp[String(l.employee_id)] = l; });

    const mapped = empArr.map((e) => mapEmployeeToRow(e, linesByEmp[String(e.id || e.employee_id)]));
    setRows(mapped);

    const autoExpand: Record<string, boolean> = {};
    mapped.forEach((m) => {
      if ((parseFloat(m.overtime) || 0) > 0 || (parseFloat(m.vacation) || 0) > 0 || (parseFloat(m.sick) || 0) > 0) {
        autoExpand[String(m.id)] = true;
      }
    });
    setExpandedRows(autoExpand);
  }, [loadedData]);

  useEffect(() => {
    setStatHolidayApplied(null);
    setRows((prev) => prev.map((r) => ({ ...r, statAvgDaily: '', statHoliday: '' })));
  }, [payRun?.pay_period_start, payRun?.pay_period_end]);

  useEffect(() => {
    if (payRun && payRun.pay_period_start && payRun.pay_period_end && !statHolidayApplied) {
      setStatHolidayPopupOpen(true);
    }
  }, [payRun, statHolidayApplied]);

  const toggleExpanded = (id: string | number): void => {
    setExpandedRows((prev) => {
      const next = { ...prev };
      const key = String(id);
      if (next[key]) delete next[key]; else next[key] = true;
      return next;
    });
  };

  const applyStatHolidayToRows = (overrides: StatHolidayOverrides): void => {
    setRows((prevRows) => {
      const next = prevRows.map((r) => {
        const o = overrides[String(r.id)];
        if (!o) return r;
        if (o.eligible && o.stat_pay_amount != null) {
          return {
            ...r,
            statAvgDaily: Number(o.stat_pay_amount).toFixed(2),
            statHoliday: r.statHoliday || '0',
          };
        }
        return r;
      });
      autosave.schedule(payRunId, next);
      return next;
    });
    setStatHolidayApplied(true);
  };

  const updateRow = <K extends keyof Row>(id: string | number, field: K, value: Row[K]): void => {
    setRows((rs) => {
      const next = rs.map((r) => (r.id === id ? { ...r, [field]: value } : r));
      autosave.schedule(payRunId, next);
      return next;
    });
  };

  const toggleIncluded = (id: string | number): void => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, included: !r.included } : r)));
  };

  const skipFromRun = (id: string | number): void => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, included: false, skipped: true } : r)));
  };

  const addBackToRun = (id: string | number): void => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, included: true, skipped: false } : r)));
  };

  const saveMemo = (id: string | number, text: string, applyAll: boolean): void => {
    setRows((rs) => rs.map((r) => {
      if (applyAll) return { ...r, memo: text };
      return r.id === id ? { ...r, memo: text } : r;
    }));
  };

  const filteredRows = useMemo(() => {
    let list = rows.filter((r) => {
      const status = r.employment_status || 'active';
      return status !== 'terminated' && status !== 'deceased' && status !== 'not_on_payroll';
    });
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter((r) => r.name.toLowerCase().includes(q));
    }
    if (statusFilter.length > 0) {
      list = list.filter((r) => {
        if (statusFilter.includes('in_crew') && r.included && r.ready) return true;
        if (statusFilter.includes('skipped') && r.skipped) return true;
        if (statusFilter.includes('not_ready') && !r.ready) return true;
        if (statusFilter.includes('desks') && !r.included && r.ready) return true;
        return false;
      });
    }
    return list;
  }, [rows, searchQuery, statusFilter]);

  const readyRows = rows.filter((r) => r.ready);
  const includedRows = rows.filter((r) => r.included && r.ready);
  const hasAnyHours = includedRows.some((r) => {
    const a = rowAmounts(r);
    return a.totalHours > 0 || (parseFloat(String(r.statAvgDaily)) || 0) > 0;
  });
  const needsHoursRows = readyRows.filter((r) => rowAmounts(r).totalHours === 0);
  const totalHours = includedRows.reduce((s, r) => s + rowAmounts(r).totalHours, 0);
  const totalGross = includedRows.reduce((s, r) => s + rowAmounts(r).gross, 0);

  const handleReview = async (): Promise<void> => {
    if (saving) return;
    if (includedRows.length === 0) {
      await confirm({ title: 'No employees selected', message: 'Check at least one employee to include in this pay run.', confirmLabel: 'Got it', hideCancel: true });
      return;
    }
    const includedWithoutHours = includedRows.filter((r) => {
      const a = rowAmounts(r);
      return a.totalHours === 0 && (parseFloat(String(r.statAvgDaily)) || 0) === 0;
    });
    if (includedWithoutHours.length > 0) {
      const names = includedWithoutHours.map((r) => r.name).join(', ');
      await confirm({ title: 'Employees missing hours', message: 'These employees have no hours entered: ' + names + '. Add hours or uncheck them to continue.', confirmLabel: 'Got it', hideCancel: true });
      return;
    }
    setSaving(true);
    setError('');
    try {
      const employeeInputs = includedRows.map((r) => {
        const a = rowAmounts(r);
        return {
          employee_id: r.id,
          hours: { regular: a.regular, overtime: a.overtime, stat_holiday: a.stat, vacation: a.vacation, sick: a.sick },
          bonus: 0, commission: 0, reimbursement: 0,
          stat_pay_amount: parseFloat(String(r.statAvgDaily)) || 0,
        };
      });
      const resp = await fetch(API_URL + '/api/v1/payroll/runs/' + payRunId + '/calculate', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ employee_inputs: employeeInputs, pay_periods_per_year: schedule ? schedule.periods_per_year : 26, subnational: 'AB' }),
      });
      if (!resp.ok) throw new Error('Calculation failed: ' + (await resp.text()));
      const runData = await resp.json();
      navigate('/payroll/run/' + payRunId + '/preview', {
        state: {
          calculation: {
            employee_count: runData.employee_count,
            total_gross: runData.total_gross,
            total_deductions: runData.total_deductions,
            total_net: runData.total_net,
            stubs: runData.stubs || [],
            source: 'backend',
          },
        },
      });
    } catch (err: any) {
      setError(err.message);
      setSaving(false);
    }
  };

  const handleSaveForLater = (): void => {
    autosave.flush(payRunId, rows);
    navigate('/payroll/overview');
  };

  const handleExport = async (): Promise<void> => {
    if (!payRunId) return;
    try {
      const res = await fetch(API_URL + '/api/v1/payroll/runs/' + payRunId + '/export', { headers: authHeaders() });
      if (!res.ok) { alert('Could not export'); return; }
      const disp = res.headers.get('content-disposition') || '';
      let filename = 'payroll_export.csv';
      const m = disp.match(/filename="?([^";]+)"?/);
      if (m) filename = m[1];
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e: any) {
      console.error('Export failed', e);
      alert('Export failed: ' + e.message);
    }
  };

  const handleEditSaved = async (): Promise<void> => {
    const empId = editDrawerEmployeeId;
    if (!empId || !payRunId) return;
    try {
      const r = await fetch(API_URL + '/api/v1/payroll/pay-runs/' + payRunId + '/paycheques/' + empId, { headers: authHeaders() });
      if (!r.ok) return;
      const d = await r.json();
      const regular = (d.earnings || []).find((e: any) => e.type === 'Regular Pay');
      const stat = (d.earnings || []).find((e: any) => e.type === 'Stat Holiday Pay');
      const statAdw = (d.earnings || []).find((e: any) => e.type === 'Stat pay - average daily wage');
      setRows((prev) => prev.map((row) => {
        if (row.id !== empId) return row;
        return {
          ...row,
          regular: regular ? String(regular.hours || 0) : row.regular,
          statHoliday: stat ? String(stat.hours || 0) : row.statHoliday,
          statAvgDaily: statAdw ? Number(statAdw.current || 0) : row.statAvgDaily,
          memo: d.memo || '',
        };
      }));
    } catch (e) {
      console.error('Refresh row failed:', e);
    }
  };

  if (queryLoading) {
    return (
      <div style={{ padding: '28px 32px', fontFamily: FONT }}>
        <div style={{ padding: 40, color: (C as any).muted }}>Loading...</div>
      </div>
    );
  }
  if (error && !payRun) {
    return (
      <div style={{ padding: '28px 32px', fontFamily: FONT }}>
        <div style={{ padding: 16, background: '#FCEBEB', borderRadius: 10, color: '#791F1F' }}>{error}</div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .novala-page-scroll::-webkit-scrollbar { display: none; }
        .novala-page-scroll { scrollbar-width: none; -ms-overflow-style: none; }
      `}</style>
      <div className="novala-page-scroll" style={{ maxWidth: '100%', margin: 0, padding: '28px 32px 110px', fontFamily: FONT, overflowY: 'auto', height: '100vh' }}>

        <div style={{ marginBottom: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: C.ink, fontWeight: 500, marginBottom: 14 }}>
            <a onClick={() => navigate('/payroll/overview')} style={{ color: C.ink, fontWeight: 600, cursor: 'pointer', opacity: 0.7 }}>Payroll</a>
            <span style={{ color: C.ink, opacity: 0.4 }}>/</span>
            <a onClick={() => navigate('/payroll/drafts')} style={{ color: C.ink, fontWeight: 600, cursor: 'pointer', opacity: 0.7 }}>Pay runs</a>
            <span style={{ color: C.ink, opacity: 0.4 }}>/</span>
            <span style={{ color: C.ink, fontWeight: 700 }}>Run payroll</span>
          </div>
          <div style={{ fontSize: 34, fontWeight: 700, color: C.ink, letterSpacing: '-0.02em', lineHeight: 1.1, marginBottom: 8 }}>Run payroll</div>
          <div style={{ fontSize: 14, color: C.ink, fontWeight: 500 }}>Review hours, then submit to finalize the run</div>
        </div>

        <RunPayrollHeader
          needsHoursRows={needsHoursRows}
          includedRows={includedRows}
          readyRows={readyRows}
          totalHours={totalHours}
          totalGross={totalGross}
          schedule={schedule}
          payRun={payRun}
          onChangePeriod={() => setChangePeriodOpen(true)}
          onAddEmployee={() => navigate('/payroll/employees')}
        />

        <RunPayrollToolbar
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          searchExpanded={searchExpanded}
          setSearchExpanded={setSearchExpanded}
          onExport={handleExport}
        />

        <div style={{ border: '1px solid ' + C.line, borderRadius: 12, background: '#fff', overflow: 'visible' }}>
          <div style={{ padding: '14px 20px', background: (C as any).page, borderBottom: '1px solid ' + C.line, display: 'grid', gridTemplateColumns: GRID_COLS, gap: 16, fontSize: 11, fontWeight: 700, color: (C as any).muted, letterSpacing: 0.4 }}>
            <div></div>
            <div></div>
            <div>EMPLOYEE · {includedRows.length} OF {readyRows.length}</div>
            <div style={{ textAlign: 'right' }}>REGULAR HOURS</div>
            <div style={{ textAlign: 'right' }}>STAT HOLIDAY HOURS</div>
            <div style={{ textAlign: 'right' }}>STAT PAY (AVG)</div>
            <div style={{ textAlign: 'right' }}>TOTAL HOURS</div>
            <div style={{ textAlign: 'right' }}>GROSS PAY</div>
            <div style={{ textAlign: 'center' }}>MEMO</div>
            <div style={{ textAlign: 'left' }}>PAY METHOD</div>
            <div></div>
          </div>

          {filteredRows.length === 0 && (
            <div style={{ padding: 30, textAlign: 'center', color: (C as any).muted, fontSize: 14 }}>No employees found.</div>
          )}

          {filteredRows.map((r, idx) => (
            <EmployeeRow
              key={String(r.id)}
              row={r}
              isLast={idx === filteredRows.length - 1}
              isExpanded={Boolean(expandedRows[String(r.id)])}
              isSelected={selectedRowId === r.id}
              openMenuId={openMenuId}
              openPayMethodId={openPayMethodId}
              onSelect={setSelectedRowId}
              onToggleExpanded={toggleExpanded}
              onToggleIncluded={toggleIncluded}
              onUpdateField={updateRow}
              onSaveMemo={saveMemo}
              onSkip={skipFromRun}
              onAddBack={addBackToRun}
              onOpenEditDrawer={setEditDrawerEmployeeId}
              onSetOpenMenuId={setOpenMenuId}
              onSetOpenPayMethodId={setOpenPayMethodId}
            />
          ))}
        </div>

        {error && <div style={{ padding: 12, background: '#FCEBEB', borderRadius: 8, color: '#791F1F', fontSize: 13, marginTop: 14 }}>{error}</div>}

        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: '#fff', borderTop: '1px solid ' + C.line, padding: '14px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 -2px 12px rgba(0,0,0,0.06)', zIndex: 20 }}>
          <button onClick={() => setShowCancelModal(true)} style={{ background: 'transparent', border: '1px solid ' + C.line, color: C.ink, fontSize: 14, fontWeight: 600, padding: '10px 20px', borderRadius: 10, cursor: 'pointer', fontFamily: FONT }}>Cancel</button>
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={handleSaveForLater} style={{ background: 'transparent', border: '1px solid ' + C.line, color: C.ink, fontSize: 14, fontWeight: 600, padding: '10px 20px', borderRadius: 10, cursor: 'pointer', fontFamily: FONT }}>Save for later</button>
            <button
              onClick={handleReview}
              disabled={saving || includedRows.length === 0 || !hasAnyHours}
              style={{
                background: (C as any).brand, color: 'white', fontSize: 14, fontWeight: 600,
                padding: '10px 22px', borderRadius: 10, border: 'none',
                cursor: (saving || includedRows.length === 0 || !hasAnyHours) ? 'not-allowed' : 'pointer',
                opacity: (saving || includedRows.length === 0 || !hasAnyHours) ? 0.5 : 1,
                fontFamily: FONT, display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {saving ? 'Calculating...' : ('Review payroll for ' + includedRows.length + ' employee' + (includedRows.length === 1 ? '' : 's'))}
              <span>→</span>
            </button>
          </div>
        </div>

        <RunPayrollModals
          payRun={payRun}
          payRunId={payRunId}
          changePeriodOpen={changePeriodOpen}
          onChangePeriodCancel={() => setChangePeriodOpen(false)}
          onChangePeriodSaved={(updated) => {
            setChangePeriodOpen(false);
            setPayRun((prev) => (prev ? { ...prev, ...updated } : prev));
            setStatHolidayApplied(null);
            setStatHolidayOverrides({});
          }}
          statHolidayPopupOpen={statHolidayPopupOpen}
          statHolidayOverrides={statHolidayOverrides}
          onStatPopupCancel={() => { setStatHolidayPopupOpen(false); navigate('/payroll/overview'); }}
          onStatPopupContinue={(overrides) => {
            setStatHolidayPopupOpen(false);
            setStatHolidayOverrides(overrides || {});
            applyStatHolidayToRows(overrides || {});
          }}
          onOpenAdjust={(emp, hol) => setStatSubModal({ type: 'adjust', emp, hol })}
          onOpenMarkNotEligible={(emp, hol) => setStatSubModal({ type: 'not_eligible', emp, hol })}
          onOpenOverrideEligible={(emp, hol) => setStatSubModal({ type: 'override_eligible', emp, hol })}
          statSubModal={statSubModal}
          onCloseStatSub={() => setStatSubModal(null)}
          onStatSubSave={(empId, patch) => {
            setStatHolidayOverrides((prev) => ({ ...prev, [empId]: { ...(prev as any)[empId], ...patch } }));
            setStatSubModal(null);
          }}
          editDrawerEmployeeId={editDrawerEmployeeId}
          onCloseEditDrawer={() => setEditDrawerEmployeeId(null)}
          onEditSaved={handleEditSaved}
        />
      </div>

      {showCancelModal && (
        <CancelPayRunModal onDismiss={() => setShowCancelModal(false)} onConfirm={() => { setShowCancelModal(false); navigate('/payroll/overview'); }} />
      )}
    </>
  );
}